



































































































































import { Component, Vue, Watch } from "@/shared/vuepack";
import { useHsabExpenseFormStore } from "@/employee/store/hsabExpenseForm";
import {
  FetchState,
  HsabExpenseFormStep,
  RecurringSchedule,
  TransferSchedule,
  TransferScheduleMonthlyByDayDetails,
  TransferScheduleType
} from "@/shared/types";
import ScheduleFrequencySelect from "@/shared/components/ui/ScheduleFrequencySelect.vue";
import { useBankHolidayStore } from "@/employer/store/bankHoliday";
import moment from "moment-timezone";
import _ from "lodash";
import * as transferScheduleUtility from "@/employee/Utilities/TransferScheduleUtility";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useHsabExpenseStore } from "@/employee/store/hsabExpense";

@Component({
  components: {
    GenericLoadingSpinner,
    ScheduleFrequencySelect
  }
})
export default class HsabExpenseFormSchedule extends Vue {
  hsabExpenseStore = useHsabExpenseStore();
  hsabExpenseFormStore = useHsabExpenseFormStore();
  bankHolidayStore = useBankHolidayStore();

  debouncedGetCalendarEntries = _.debounce(_ => this.hsabExpenseFormStore.loadExpenseScheduleCalendarEntries(), 250);

  get isPreviewLoading () {
    return this.hsabExpenseFormStore.isSchedulePreviewLoading;
  }

  get formModel (): TransferSchedule {
    return this.hsabExpenseFormStore.expenseForm.schedule;
  }

  get existingSchedule (): TransferSchedule {
    if (!this.hsabExpenseFormStore.expense) {
      return null;
    }
    return this.hsabExpenseFormStore.expense.schedule;
  }

  get isEditingExistingSchedule (): boolean {
    return this.existingSchedule !== null;
  }

  get areHolidaysLoaded (): boolean {
    return this.bankHolidayStore.bankHolidaysInYearLoaded(this.currentYear)
        && this.bankHolidayStore.bankHolidaysInYearLoaded(this.currentYear + 1);
  }

  get isMissingOptions () {
    if (!this.formModel) {
      return true;
    }
    return (this.formModel.scheduleType === TransferScheduleType.Weekly && this.formModel.weeklyDetails.length < 1)
        || (this.formModel.scheduleType === TransferScheduleType.MonthlyByDay && this.formModel.monthlyByDayDetails.length < 1)
        || (this.formModel.scheduleType === TransferScheduleType.MonthlyByDate && this.formModel.monthlyByDateDetails.length < 1);
  }

  get disableBeforeDateInclusive (): string {
    return this.firstValidStartDate.clone().add(-1, "days").format("MM/DD/YYYY");
  }

  get disableAfterDate (): string {
    return `12/31/${this.currentYear + 1}`;
  }

  get federalHolidays (): string[] {
    return transferScheduleUtility.federalHolidays();
  }

  get isEditingDisabled () {
    return this.hsabExpenseFormStore.isReadonly;
  }

  get isEditingStartDateDisabled () {
    let hasScheduleAlreadyStarted = false;
    if (this.isEditingExistingSchedule) {
      const nowCST = moment().tz("America/Chicago");
      const originalStartDate = moment(this.existingSchedule.scheduleStartDate);
      hasScheduleAlreadyStarted = originalStartDate.isBefore(nowCST, "day")
          || (originalStartDate.isSame(nowCST, "day") && nowCST.hour() >= this.hsabExpenseStore.recurringScheduleProcessingStartHour);
    }

    return this.hsabExpenseFormStore.isReadonly || hasScheduleAlreadyStarted;
  }

  get currentYear () {
    return moment().year();
  }

  get description () {
    return this.hsabExpenseFormStore.scheduleDescription;
  }

  get submitButtonTooltip () {
    if (this.isMissingOptions || !this.isPreviewLoaded) {
      return "";
    }

    const noValidDates = this.showPreviewColumn && this.previewDates.length === 0;
    if (noValidDates) {
      return "There are no valid dates for this schedule";
    }

    return "";
  }

  get disableSubmitButton () {
    if (this.isMissingOptions || !this.isPreviewLoaded) {
      return true;
    }

    const noValidDates = this.showPreviewColumn && this.previewDates.length === 0;
    if (noValidDates) {
      return true;
    }

    return false;
  }

  get startDateValidation () {
    return {
      required: true,
      isWeekday: true,
      isNotFederalHoliday: this.federalHolidays,
      validateDateIsAfter: this.firstValidStartDate.format("MM/DD/YYYY")
    };
  }

  get firstValidStartDate (): moment.Moment {
    return transferScheduleUtility.firstValidStartDateFromNow();
  }

  get selectedSchedule (): RecurringSchedule {
    const schedule: RecurringSchedule = {
      ...transferScheduleUtility.defaultValues.everyWeekRecurringSchedule,
      scheduleType: this.formModel.scheduleType,
      scheduleInterval: this.formModel.scheduleInterval
    };

    switch (this.formModel.scheduleType) {
      case (TransferScheduleType.Weekly):
        schedule.days = this.formModel.weeklyDetails.map(details => details.dayOfWeek);
        break;

      case (TransferScheduleType.MonthlyByDay):
        schedule.dayOfWeek = this.formModel.monthlyByDayDetails[0].dayOfWeek;
        schedule.scheduleMonthBoundary = this.formModel.monthlyByDayDetails[0].monthBoundary;
        break;

      case (TransferScheduleType.MonthlyByDate):
        schedule.dates = this.formModel.monthlyByDateDetails.map(details => {
          return details.isLastDay ? "Last" : details.dayOfMonth.toString();
        });
        break;

      default:
        break;
    }

    return schedule;
  }

  set selectedSchedule (value: RecurringSchedule) {
    const monthlyByDayDetails: TransferScheduleMonthlyByDayDetails[] = [
      {
        monthBoundary: value.scheduleMonthBoundary,
        dayOfWeek: value.dayOfWeek
      }
    ];
    switch (value.scheduleType) {
      case (TransferScheduleType.Weekly):

        this.hsabExpenseFormStore.expenseForm.schedule.weeklyDetails = value.days.map(weekday => ({
          dayOfWeek: weekday
        }));
        break;

      case (TransferScheduleType.MonthlyByDay):
        this.hsabExpenseFormStore.expenseForm.schedule.monthlyByDayDetails = _.cloneDeep(monthlyByDayDetails);
        break;

      case (TransferScheduleType.MonthlyByDate):
        this.hsabExpenseFormStore.expenseForm.schedule.monthlyByDateDetails = value.dates.map(date => ({
          dayOfMonth: date === "Last" ? null : +date,
          isLastDay: date === "Last" ? true : false
        }));

        break;
    }
    this.hsabExpenseFormStore.expenseForm.schedule.scheduleInterval = value.scheduleInterval;
    this.hsabExpenseFormStore.expenseForm.schedule.scheduleType = value.scheduleType;
  }

  get showPreviewColumn () {
    return this.hsabExpenseFormStore.calendarEntriesFetchState !== FetchState.Uninitialized;
  }

  get isPreviewLoaded () {
    return this.hsabExpenseFormStore.isSchedulePreviewLoaded;
  }

  get previewDates () {
    return this.hsabExpenseFormStore.generatedCalendarEntries.calendarDates;
  }

  get first10PreviewDates () {
    return this.previewDates.slice(0, 10);
  }

  @Watch("areHolidaysLoaded")
  onAreHolidaysLoadedChanged (newVal, oldVal) {
    if (oldVal === false &&
        newVal === true &&
        this.hsabExpenseFormStore.expenseForm.isRecurringTemplate &&
        this.hsabExpenseFormStore.expenseForm.schedule === null) {
      this.hsabExpenseFormStore.setDefaultSchedule();
    }
  }

  @Watch("formModel", { deep: true })
  updateScheduleDescription () {
    if (!this.formModel) {
      return;
    }

    this.hsabExpenseFormStore.scheduleDescription = transferScheduleUtility.getScheduleDescription(this.formModel);
  }

  created () {
    this.bankHolidayStore.loadBankHolidays(this.currentYear);
    this.bankHolidayStore.loadBankHolidays(this.currentYear + 1);
  }

  mounted () {
    this.$validator.extend("isWeekday", transferScheduleUtility.validationObjects.isWeekday);
    this.$validator.extend("isNotFederalHoliday", transferScheduleUtility.validationObjects.isNotFederalHoliday);
    this.$validator.extend("validateDateIsAfter", transferScheduleUtility.validationObjects.validateDateIsAfter);
  }

  @Watch("formModel", { deep: true })
  updateProjectedDates () {
    if (!this.formModel || this.isMissingOptions) {
      this.hsabExpenseFormStore.clearCurrentCalendarEntries();
      return;
    }

    const endDate = this.formModel.scheduleEndDate ?
        this.formModel.scheduleEndDate : `${this.currentYear + 1}-12-31`;

    const args = {
      proposedSchedule: _.cloneDeep(this.formModel),
      toDate: endDate,
      numberOfCalendarEntries: 3
    };
    this.debouncedGetCalendarEntries(args);
  }

  onBack () {
    this.hsabExpenseFormStore.navigateBack();
  }

  onSubmit () {
    this.hsabExpenseFormStore.formStepToDisplay = HsabExpenseFormStep.confirmation;
  }

  getPreviewDateDisplay (str) {
    return moment(str).format("dddd MMMM Do, YYYY");
  }
}
